import React, {useContext, useState, useEffect} from 'react';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';
import moment from 'moment';

import sy from '~/styles';
import UserContext from '~/components/context/UserContext';
import {
  DealerPersona,
  Text,
  Pressable,
  Dialog,
  ExpiredWarning,
  NotificationImportantIcon,
  Avatar,
} from '~/components/controls';
import {useDispatch, useSelector} from '~/lib/hooks';
import {
  setShowDrawer,
  showRefreshDialog,
  showRefreshImportantDialog,
} from '~/actions';
import {service_request as serviceRequestApi} from '~/api/private';
import {EXPIRED_STATUS} from '~/types/statuses';
import {throttle} from '~/lib/utils';

import UpdateIcon from '~/images/md-icons/update/materialicons/24px.svg';
import PropTypes from 'prop-types';

const doFetchExpired = throttle(async () => {
  const response = await serviceRequestApi.expired();
  return response?.expired;
});

const doFetchImportantNotification = throttle(async () => {
  const result = await serviceRequestApi.fetch_important_notification();
  return result;
});

const deriveHeaderTitle = (title, companies, me) => {
  if (title) {
    return title;
  }

  if (companies.length === 1) {
    return companies[0];
  }

  if (me?.dealers.length === me?.dealer_selection?.length) {
    return 'Alle vestigingen';
  }

  return 'Geen vestiging';
};

const HeaderAction = ({
  urgent_refresh_required,
  refresh_required,
  importantNotification,
  expired,
  onShowImportant,
  onShowRefresh,
  onShowRefreshImportant,
}) => {
  if (urgent_refresh_required) {
    return (
      <Pressable onPress={onShowRefreshImportant}>
        <UpdateIcon fill="#2907E3" width="24" height="24" />
      </Pressable>
    );
  }

  if (refresh_required) {
    return (
      <Pressable onPress={onShowRefresh}>
        <UpdateIcon fill="#2907E3" width="24" height="24" />
      </Pressable>
    );
  }

  if (importantNotification?.message?.length > 0) {
    return (
      <Pressable onPress={onShowImportant}>
        <NotificationImportantIcon {...importantNotification} />
      </Pressable>
    );
  }

  if (expired?.[EXPIRED_STATUS.EXPIRED_60_DAYS] > 0) {
    return (
      <ExpiredWarning
        type={EXPIRED_STATUS.EXPIRED_60_DAYS}
        count={expired?.[EXPIRED_STATUS.EXPIRED_60_DAYS]}
      />
    );
  }

  if (expired?.[EXPIRED_STATUS.EXPIRED_30_DAYS] > 0) {
    return (
      <ExpiredWarning
        type={EXPIRED_STATUS.EXPIRED_30_DAYS}
        count={expired?.[EXPIRED_STATUS.EXPIRED_30_DAYS]}
      />
    );
  }

  return null;
};

HeaderAction.propTypes = {
  urgent_refresh_required: PropTypes.bool,
  refresh_required: PropTypes.bool,
  importantNotification: PropTypes.shape({
    message: PropTypes.string,
    title: PropTypes.string,
    updated: PropTypes.string,
  }),
  expired: PropTypes.object,
  onShowImportant: PropTypes.func,
  onShowRefresh: PropTypes.func,
  onShowRefreshImportant: PropTypes.func,
};

const DealerHeader = (props) => {
  const dispatch = useDispatch();
  const {me} = useContext(UserContext);

  const update_important_message = useSelector(
    (state) => state.update_important_message,
  );

  const refresh_required = useSelector((state) => state.refresh_required);

  const urgent_refresh_required = useSelector(
    (state) => state.urgent_refresh_required,
  );

  const [importantNotification, setImportantNotification] = useState(null);
  const [showImportantNotification, setShowImportantNotification] =
    useState(false);
  const [expired, setExpired] = useState(null);

  useEffect(() => {
    if (me) {
      doFetchImportantNotification().then(setImportantNotification);
    }
  }, [me, update_important_message]);

  useEffect(() => {
    if (me) {
      doFetchExpired().then(setExpired);
    }
  }, [me]);

  const companies = [
    ...new Set(
      me?.dealers
        .filter((item) => me?.dealer_selection.includes(item.id))
        .map((item) => item.company_name),
    ),
  ];
  const title = deriveHeaderTitle(props.title, companies, me);

  return (
    <>
      <View
        style={[
          sy['flex-row'],
          sy['items-center'],
          sy['justify-between'],
          props.style,
        ]}>
        <Pressable onPress={() => dispatch(setShowDrawer(true))}>
          {companies.length === 1 ? (
            <DealerPersona company_name={companies[0]} size={32} />
          ) : (
            <Avatar first_name={me?.first_name} last_name={me?.last_name} />
          )}
        </Pressable>

        <Text style={[sy.xxLarge, sy['text-center'], props.titleStyle]}>
          {title}
        </Text>

        <View
          style={[
            {width: 36, height: 36},
            sy['items-end'],
            sy['justify-center'],
          ]}>
          <HeaderAction
            urgent_refresh_required={urgent_refresh_required}
            refresh_required={refresh_required}
            importantNotification={importantNotification}
            expired={expired}
            onShowImportant={() => setShowImportantNotification(true)}
            onShowRefresh={() => dispatch(showRefreshDialog())}
            onShowRefreshImportant={() =>
              dispatch(showRefreshImportantDialog())
            }
          />
        </View>
      </View>
      <Dialog
        visible={showImportantNotification}
        title={importantNotification?.title ?? 'Belangrijk bericht'}
        titleIcon={<NotificationImportantIcon {...importantNotification} />}
        onDismiss={() => setShowImportantNotification(false)}
        options={{
          noPaddingContent: true,
        }}>
        <View style={[sy['py-4'], sy['px-6']]}>
          <Text>
            <div
              dangerouslySetInnerHTML={{
                __html: importantNotification?.message,
              }}
            />
          </Text>
        </View>
        <Divider style={sy['bg-gray88']} />
        <View style={[sy['py-4'], sy['px-6']]}>
          <Text style={sy.smallRegular}>
            Laatste update:&nbsp;
            {moment(importantNotification?.updated).format('DD/MM/YYYY HH:mm')}
          </Text>
        </View>
      </Dialog>
    </>
  );
};
DealerHeader.propTypes = {
  title: PropTypes.string,
  style: PropTypes.any,
  titleStyle: PropTypes.any,
};

export default DealerHeader;
